import { SearchOptionLinksObject } from 'components/SA/SearchProvider/types'
import { get as getStorage, set as setStorage } from 'lib/localStorage'
import { useEffect, useState } from 'react'

const RECENT_SEARCH_KEY = 'sa_recent_searches'

type RecentSearchDataObject = {
  label: string
  link: string
  unixTimestamp: number
}

type UseRecentSearchesParams = {
  userId: string
}

const useRecentSearches = ({ userId }: UseRecentSearchesParams) => {
  const [recentSearches, setRecentSearches] = useState<ReadonlyArray<SearchOptionLinksObject>>([])

  const getRecentSearchesFromLocalStorage = (): Record<
    string,
    ReadonlyArray<RecentSearchDataObject>
  > => {
    const localStorageRecentSeaches = getStorage(RECENT_SEARCH_KEY)
    return localStorageRecentSeaches || {}
  }

  const getRecentSearchesByIdFromLocalStorage = (
    userId: string
  ): ReadonlyArray<RecentSearchDataObject> => {
    const localStorageRecentSeaches = getStorage(RECENT_SEARCH_KEY)
    return Array.isArray(localStorageRecentSeaches?.[userId])
      ? localStorageRecentSeaches?.[userId]
      : []
  }

  const setRecentSearchesInLocalStorage = (
    newRecentSearches: ReadonlyArray<RecentSearchDataObject>,
    userId: string
  ) => {
    if (Boolean(userId)) {
      const recentSearches = getRecentSearchesFromLocalStorage()
      setStorage(RECENT_SEARCH_KEY, { ...recentSearches, [userId]: newRecentSearches })
    }
  }

  const updateRecentSearches = (
    mergedRecentSearches: ReadonlyArray<RecentSearchDataObject>,
    userId: string
  ) => {
    if (Boolean(userId)) {
      setRecentSearchesInLocalStorage(mergedRecentSearches, userId)
      const withoutTimestamp = mergedRecentSearches.map(({ label, link }) => ({ label, link }))
      setRecentSearches(withoutTimestamp)
    }
  }

  const addRecentSearch = ({ label, link }: SearchOptionLinksObject, userId: string) => {
    if (Boolean(userId)) {
      const fromLocalStorage = getRecentSearchesByIdFromLocalStorage(userId)
      const localStorageRecentSearches = Array.isArray(fromLocalStorage) ? fromLocalStorage : []

      const filtered = localStorageRecentSearches.filter((item: any) => item.label !== label)

      const newOption = {
        label,
        link,
        unixTimestamp: new Date().getTime(),
      }

      const mergedRecentSearches = [newOption, ...filtered]

      if (mergedRecentSearches.length > 20) {
        mergedRecentSearches.pop()
      }

      updateRecentSearches(mergedRecentSearches, userId)
    }
  }

  const removeRecentSearch = (title: string, userId: string) => {
    if (Boolean(userId)) {
      const localStorageRecentSearches = getRecentSearchesByIdFromLocalStorage(userId)
      const filteredRecentSearches = localStorageRecentSearches.filter(
        ({ label }) => label !== title
      )
      updateRecentSearches(filteredRecentSearches, userId)
    }
  }

  useEffect(() => {
    const localStorageRecentSearches = getRecentSearchesByIdFromLocalStorage(userId)
    setRecentSearches(localStorageRecentSearches)
  }, [userId])

  return { recentSearches, getRecentSearchesFromLocalStorage, addRecentSearch, removeRecentSearch }
}

export default useRecentSearches
